/**
 * Typography
 * - Figma: https://www.figma.com/file/36q2en2Fb6AAepdm1I6NGn/Design-system?node-id=62%3A19532
 * - Zeroheight: https://zeroheight.com/3fe730a39/v/0/p/6908c7-typography
 */
import { css, CSSObject, FlattenSimpleInterpolation } from 'styled-components';

import { pxToRem } from 'utils/convert';
import { borderRadius } from '../borderRadius';
import { colors } from '../colors';
import { duration, easing } from '../motion';
import * as spacing from '../spacing';

export const fonts = {
  sansSerif:
    '"Sofia Pro", system-ui, -apple-system, BlinkMacSystemFont, "Avenir Next", Avenir, "Segoe UI", "Lucida Grande", "Helvetica Neue", Helvetica, "Fira Sans", Roboto, Noto, "Droid Sans", Cantarell, Oxygen, Ubuntu, "Franklin Gothic Medium", "Century Gothic", "Liberation Sans", sans-serif',
  monospaced: 'Menlo, Monaco, Consolas, "Lucida Console", monospace',
  roboto: '"Roboto", sans-serif',
  sfPro: '"SF Pro", system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
};
export type Fonts = {
  text: string;
  code: string;
};

export const fontSizes = {
  s11: pxToRem(11),
  s12: pxToRem(12),
  s13: pxToRem(13),
  s14: pxToRem(14),
  s15: pxToRem(15),
  s16: pxToRem(16),
  s18: pxToRem(18),
  s20: pxToRem(20),
  s22: pxToRem(22),
  s24: pxToRem(24),
  s28: pxToRem(28),
  s34: pxToRem(34),
  s40: pxToRem(40),
  s48: pxToRem(48),
  s56: pxToRem(56),
};
export type FontSizes = typeof fontSizes;

// https://developer.mozilla.org/en-US/docs/Web/CSS/font-weight
// Style Linking: https://www.fontspring.com/support/style-linking
// style-linked to font files in src/modules/app/components/GlobalStyles.js
export const fontWeights = {
  light: 300, // style linked to 'light' font file
  regular: 400, // style linked to 'normal' font file (Normal, Regular)
  medium: 500, // style linked to 'medium' font file
  semiBold: 600, // style linked to 'semi-bold' font file (Semi Bold, Demi Bold)
  bold: 700, // style linked to 'bold' font file
};
export type FontWeights = typeof fontWeights;

export const letterSpacing = {
  neg125: pxToRem(-1.25),
  neg75: pxToRem(-0.75),
  neg25: pxToRem(-0.25),
  default: pxToRem(0),
  pos10: pxToRem(0.1),
  pos200: pxToRem(2),
};
export type LetterSpacing = typeof letterSpacing;

// adjust as necessary, keep in mind the relative stacking order of the element
export const zIndex = {
  drawer: 401,
  drawerOverlay: 10001,
  dropdown: 11,
  modal: 10002,
  modalOverlay: 10001,
  nav: 501,
  popover: 10003,
  popup: 201,
  toast: 11001,
  newDropdown: 11000, // this is meant replace the old dropdown zIndex
  stickyInput: 11002,
  toolbar: 6,
  tooltip: 11001,
  loadingOverlay: 10004,
  slideOver: 10001, // 1 less than modal
  slideOverOverlay: 10000, // 1 less than modalOverlay
  takeOver: 10000, // 1 less than modalOverlay
};
export type ZIndex = typeof zIndex;

const base = {
  color: colors.textPrimary,
  fontFamily: fonts.sansSerif,
  fontWeight: fontWeights.regular,
  letterSpacing: letterSpacing.default,
};
export type BaseStyle = typeof base;
export type ComponentStyle = {
  fontSize: string;
  lineHeight?: string;
} & BaseStyle;
export type Headings = {
  heading1: ComponentStyle;
  heading2: ComponentStyle;
  heading3: ComponentStyle;
  heading4: ComponentStyle;
};
export type Body = {
  body1: ComponentStyle;
  body1Strong: ComponentStyle;
  body2: ComponentStyle;
  body2Strong: ComponentStyle;
};
export type Links = {
  link1: FlattenSimpleInterpolation;
  link2: FlattenSimpleInterpolation;
};
export type Monospace = {
  monospace1: ComponentStyle;
  monospace2: ComponentStyle;
};
export type Buttons = {
  button: ComponentStyle;
};
export type Subtitles = {
  subtitle1: ComponentStyle;
  subtitle2: ComponentStyle;
};
export type Captions = {
  caption1: ComponentStyle;
};
export type OverlineType = CSSObject & {
  fontWeight: number;
  letterSpacing: string;
  textTransform: string;
  fontSize: string;
  lineHeight: string;
};
export type Overlines = {
  overline1: OverlineType;
};

//
// ─── HEADER ─────────────────────────────────────────────────────────────────────
//

export const heading1 = {
  ...base,
  fontSize: fontSizes.s48,
  fontWeight: fontWeights.semiBold,
  letterSpacing: letterSpacing.neg125,
  lineHeight: fontSizes.s56,
};

export const heading2 = {
  ...base,
  fontSize: fontSizes.s34,
  fontWeight: fontWeights.semiBold,
  letterSpacing: letterSpacing.neg75,
  lineHeight: fontSizes.s40,
};

export const heading3 = {
  ...base,
  fontSize: fontSizes.s24,
  fontWeight: fontWeights.semiBold,
  letterSpacing: letterSpacing.neg25,
  lineHeight: fontSizes.s28,
};

export const heading4 = {
  ...base,
  fontSize: fontSizes.s20,
  fontWeight: fontWeights.semiBold,
  lineHeight: fontSizes.s24,
};

export const heading5 = {
  ...base,
  fontSize: fontSizes.s18,
  fontWeight: fontWeights.semiBold,
  lineHeight: fontSizes.s20,
};

//
// ─── BODY ───────────────────────────────────────────────────────────────────────
//

export const body1 = {
  ...base,
  fontSize: fontSizes.s16,
  lineHeight: fontSizes.s24,
  letterSpacing: letterSpacing.pos10,
};

export const body1Medium = {
  ...body1,
  fontWeight: fontWeights.medium,
};

export const body1Bold = {
  ...body1,
  fontWeight: fontWeights.semiBold,
};

export const body2 = {
  ...base,
  fontSize: fontSizes.s14,
  lineHeight: fontSizes.s20,
  letterSpacing: letterSpacing.pos10,
};

export const body2Medium = {
  ...body2,
  fontWeight: fontWeights.medium,
};

export const body2Bold = {
  ...body2,
  fontWeight: fontWeights.semiBold,
};

//
// ─── LINK ───────────────────────────────────────────────────────────────────────
//

const baseLink = css`
  color: ${colors.link};
  position: relative;
  font-weight: 500;
  text-decoration: underline;
  text-decoration-thickness: ${pxToRem(1.5)};
  text-decoration-color: ${colors.borderInteractiveSubtle};
  text-underline-offset: 2px;
  transition-property: color, text-decoration-color;
  transition-duration: ${duration.short};
  transition-timing-function: ${easing.entrance};
  cursor: pointer;
  border-radius: ${borderRadius.sm};
  outline-offset: ${spacing.eighth};

  &:hover {
    color: ${colors.linkHover};
    text-decoration: underline;
    text-decoration-thickness: ${pxToRem(1.5)};
    text-decoration-color: ${colors.borderInteractive};
  }

  &:active,
  :focus {
    color: ${colors.linkActive};
    text-decoration: underline;
    text-decoration-thickness: ${pxToRem(1.5)};
    text-decoration-color: ${colors.borderInteractiveStrong};
  }

  &:disabled {
    color: ${colors.linkDisabled};
  }

  &:focus-visible {
    text-decoration: none;
    outline: ${spacing.eighth} solid ${colors.borderInteractive};
    color: ${colors.linkFocus};
  }
`;

export const monochromeLink = css`
  color: ${colors.textSecondary};
  position: relative;
  font-weight: 500;
  text-decoration: underline;
  text-decoration-thickness: ${pxToRem(1.5)};
  text-decoration-color: ${colors.borderPrimary};
  text-underline-offset: 2px;
  transition-property: color, text-decoration-color;
  transition-duration: ${duration.short};
  transition-timing-function: ${easing.entrance};
  cursor: pointer;
  border-radius: ${borderRadius.sm};
  outline-offset: ${spacing.eighth};

  &:hover {
    color: ${colors.textPrimary};
    text-decoration: underline;
    text-decoration-thickness: ${pxToRem(1.5)};
    text-decoration-color: ${colors.borderEmphasis};
  }

  &:active,
  :focus-within {
    color: ${colors.textPrimary};
    text-decoration: underline;
    text-decoration-thickness: ${pxToRem(1.5)};
    text-decoration-color: ${colors.borderEmphasis};
  }

  &:disabled {
    color: ${colors.textDisabled};
  }

  &:focus-visible {
    text-decoration: none;
    outline: ${spacing.eighth} solid ${colors.borderStrong};
    color: ${colors.textPrimary};
  }
`;

export const link = css`
  ${body1}
  ${baseLink}
`;

export const link2 = css`
  ${body2}
  ${baseLink}
`;

//
// ─── BUTTON ─────────────────────────────────────────────────────────────────────
//
// button heights no longer take line heights. They get explicit sizing
// with flexbox layout to make sure icons don't force vertical stretching
export const button = {
  ...base,
  fontSize: fontSizes.s14,
  letterSpacing: letterSpacing.pos10,
};

//
// ─── SUBTITLES ──────────────────────────────────────────────────────────────────
//

export const subtitle1 = {
  ...base,
  fontSize: fontSizes.s16,
  fontWeight: fontWeights.semiBold,
  lineHeight: fontSizes.s20,
};

export const subtitle2 = {
  ...base,
  fontSize: fontSizes.s14,
  fontWeight: fontWeights.semiBold,
  lineHeight: fontSizes.s20,
};

//
// ─── OTHER ──────────────────────────────────────────────────────────────────────
//

export const caption = {
  ...base,
  fontSize: fontSizes.s12,
  fontWeight: fontWeights.regular,
  lineHeight: fontSizes.s16,
  letterSpacing: letterSpacing.pos10,
};

export const captionMedium = {
  ...caption,
  fontWeight: fontWeights.medium,
};

export const captionBold = {
  ...caption,
  fontWeight: fontWeights.semiBold,
};

export const captionLink = css`
  ${captionMedium}
  ${baseLink}
`;

//
// ─── MONOSPACE ──────────────────────────────────────────────────────────────────
//

export const monospace1 = {
  ...body1,
  fontFamily: fonts.monospaced,
};

export const monospace2 = {
  ...body2,
  fontFamily: fonts.monospaced,
};

export const monospaceCaption = {
  ...caption,
  fontFamily: fonts.monospaced,
};

export const code = {
  fontFamily: fonts.monospaced,
  color: colors.textPrimary,
  paddingRight: spacing.quarter,
  paddingLeft: spacing.quarter,
  backgroundColor: colors.backgroundSecondary,
  borderRadius: borderRadius.sm,
};

/**
 * Assigning text like `"uppercase"` will widen types to `string`: see https://github.com/frenic/csstype/issues/38
 */
export const overline: OverlineType = {
  fontSize: fontSizes.s11,
  fontWeight: fontWeights.semiBold,
  lineHeight: fontSizes.s12,
  letterSpacing: letterSpacing.pos200,
  textTransform: 'uppercase',
  fontFamily: fonts.sansSerif,
};

interface LabelProps extends React.HTMLProps<HTMLLabelElement> {
  /** Is the color muted? */
  isMuted?: boolean;
  /** Is field required? */
  isRequired?: boolean;
}

export const label = css`
  ${body2}
  color: ${({ isMuted }: LabelProps) => (isMuted ? colors.textSecondary : colors.textPrimary)};
  font-weight: ${fontWeights.semiBold};

  :after {
    color: ${colors.red100};
    content: '*';
    display: ${({ isRequired }: LabelProps) => (isRequired ? 'block' : 'none')};
    margin-left: ${spacing.single};
  }
`;
